import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ProductTable } from './components/ProductTable';
import { ProductDetails } from './components/ProductDetails';
import { ProductModal } from './components/ProductModal';
import { productService } from '../../../services/productos/productService';
import { Loader2, AlertCircle } from 'lucide-react';

const DEFAULT_PAGE_SIZE = 5;
const PAGE_SIZE_OPTIONS = [5, 10];

const Products = () => {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterProductType, setFilterProductType] = useState('');
  const [filterManufacturer, setFilterManufacturer] = useState('');
  const [filterFamily, setFilterFamily] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [productToDelete, setProductToDelete] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Fetch products
  const { 
    data: products = [], 
    isLoading: isLoadingProducts, 
    isError: isErrorProducts,
    error: productsError 
  } = useQuery({
    queryKey: ['products'],
    queryFn: () => productService.getProducts(),
    retry: 1,
  });

  // Fetch product types (complete with features)
  const { 
    data: productTypes = [], 
    isLoading: isLoadingTypes,
    isError: isErrorTypes,
    error: typesError 
  } = useQuery({
    queryKey: ['productTypes'],
    queryFn: () => productService.getProductTypesComplete(),
    retry: 1,
  });

  // Fetch product details when a product is selected
  const {
    data: selectedProductDetails,
    isLoading: isLoadingProductDetails,
    isError: isErrorProductDetails,
    error: productDetailsError
  } = useQuery({
    queryKey: ['product', selectedProductId],
    queryFn: () => productService.getProductWithDocs(selectedProductId),
    enabled: !!selectedProductId, // Only fetch when selectedProductId is set
    retry: 1,
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (productData) => productService.createProduct(productData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setIsModalOpen(false);
      // Don't clear selectedProductId to keep the details panel open if a product was selected
    },
    onError: (error) => {
      console.error('Error creating product:', error);
      throw error;
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => productService.updateProduct(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product', selectedProductId] });
      // Keep selectedProductId to refresh the details panel
    },
    onError: (error) => {
      console.error('Error updating product:', error);
      throw error;
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => productService.deleteProduct(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setProductToDelete(null);
      // Clear selected product if it was deleted
      if (selectedProductId === productToDelete) {
        setSelectedProductId(null);
      }
    },
    onError: (error) => {
      console.error('Error deleting product:', error);
      setProductToDelete(null);
      alert(error.message || 'Error deleting product');
    },
  });

  const handleCreate = () => {
    setSelectedProductId(null);
    setIsModalOpen(true);
  };

  const handleDelete = (productId) => {
    if (window.confirm('Are you sure you want to delete this product? This action cannot be undone.')) {
      setProductToDelete(productId);
      deleteMutation.mutate(productId);
    }
  };

  const handleSaveCreate = async (productData) => {
    await createMutation.mutateAsync(productData);
  };

  const handleSaveUpdate = async (productData) => {
    if (selectedProductId) {
      await updateMutation.mutateAsync({
        id: selectedProductId,
        data: productData
      });
    }
  };

  const handleSaveDuplicate = async (productData) => {
    await createMutation.mutateAsync(productData);
  };

  const handleProductClick = (product) => {
    setSelectedProductId(product.id);
  };

  const handleDocumentUploaded = () => {
    queryClient.invalidateQueries({ queryKey: ['product', selectedProductId] });
  };

  // Extract unique values for filters
  const uniqueProductTypes = React.useMemo(() => {
    const types = [...new Set(products.map(p => p.product_type).filter(Boolean))].sort();
    return types;
  }, [products]);

  const uniqueManufacturers = React.useMemo(() => {
    const manufacturers = [...new Set(products.map(p => p.manufacturer).filter(Boolean))].sort();
    return manufacturers;
  }, [products]);

  const uniqueFamilies = React.useMemo(() => {
    const families = [...new Set(products.map(p => p.family).filter(Boolean))].sort();
    return families;
  }, [products]);

  // Filter products based on search query and filters
  const filteredProducts = useMemo(() => {
    let filtered = products;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(product =>
        product.name?.toLowerCase().includes(query) ||
        product.product_type?.toLowerCase().includes(query) ||
        product.manufacturer?.toLowerCase().includes(query) ||
        product.family?.toLowerCase().includes(query) ||
        product.description?.toLowerCase().includes(query)
      );
    }

    if (filterProductType) {
      filtered = filtered.filter(product => product.product_type === filterProductType);
    }

    if (filterManufacturer) {
      filtered = filtered.filter(product => product.manufacturer === filterManufacturer);
    }

    if (filterFamily) {
      filtered = filtered.filter(product => product.family === filterFamily);
    }

    return filtered;
  }, [products, searchQuery, filterProductType, filterManufacturer, filterFamily]);

  // Pagination logic
  const totalItems = filteredProducts.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const paginatedProducts = useMemo(
    () => filteredProducts.slice((page - 1) * pageSize, page * pageSize),
    [filteredProducts, page, pageSize]
  );
  const rangeStart = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, totalItems);

  // Reset page to 1 when filters or page size change
  useEffect(() => {
    setPage(1);
  }, [searchQuery, filterProductType, filterManufacturer, filterFamily, pageSize]);

  const isLoading = isLoadingProducts || isLoadingTypes;
  const isError = isErrorProducts || isErrorTypes;
  const error = productsError || typesError;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading products...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-400 mb-2">Error loading products</p>
          <p className="text-gray-400 text-sm">{error?.message || 'Unknown error'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 w-full min-w-0">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-2">Products Management</h1>
        <p className="text-gray-400">Manage products and their specifications</p>
      </div>

      {/* Layout de dos columnas: Tabla izquierda y Detalles derecha */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 items-stretch">
        {/* Columna izquierda: Tabla de productos */}
        <div className="min-w-0 flex flex-col min-h-0">
          <ProductTable
            products={paginatedProducts}
            totalItems={totalItems}
            totalPages={totalPages}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            filterProductType={filterProductType}
            setFilterProductType={setFilterProductType}
            filterManufacturer={filterManufacturer}
            setFilterManufacturer={setFilterManufacturer}
            filterFamily={filterFamily}
            setFilterFamily={setFilterFamily}
            uniqueProductTypes={uniqueProductTypes}
            uniqueManufacturers={uniqueManufacturers}
            uniqueFamilies={uniqueFamilies}
            onProductClick={handleProductClick}
            onCreate={handleCreate}
            selectedProductId={selectedProductId}
          />
        </div>

        {/* Columna derecha: Detalles del producto */}
        <div className="min-w-0 flex flex-col min-h-0 lg:sticky lg:top-6">
          <ProductDetails
            product={selectedProductDetails}
            loading={isLoadingProductDetails}
            onDelete={handleDelete}
            productTypes={productTypes}
            onSaveUpdate={handleSaveUpdate}
            onSaveDuplicate={handleSaveDuplicate}
            onDocumentUploaded={handleDocumentUploaded}
          />
        </div>
      </div>

      {/* Modal para crear producto */}
      {isModalOpen && (
        <ProductModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            // Solo limpiar selectedProductId si no hay producto seleccionado
            if (!selectedProductId) {
              setSelectedProductId(null);
            }
          }}
          productTypes={productTypes}
          onSave={handleSaveCreate}
        />
      )}
    </div>
  );
};

export default Products;
